import {
  type InfiniteData,
  infiniteQueryOptions,
  keepPreviousData,
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { api, type ListResponse } from "@/api/client"
import type { KnowledgeFolder } from "@/api/generated/KnowledgeFolder"
import type { OrchestratorPayload } from "@/api/generated/OrchestratorPayload"
import type { ProjectActivityEntry } from "@/api/generated/ProjectActivityEntry"
import type { ProjectDataEntry } from "@/api/generated/ProjectDataEntry"
import type { ProjectDetail } from "@/api/generated/ProjectDetail"
import type { ProjectDocument } from "@/api/generated/ProjectDocument"
import type { ProjectFileUpload } from "@/api/generated/ProjectFileUpload"
import type { ProjectRule } from "@/api/generated/ProjectRule"
import type { ProjectSummary } from "@/api/generated/ProjectSummary"
import { useApiMutation } from "@/api/mutation"

export const PROJECT_STATUSES = ["active", "completed", "archived"] as const
export type ProjectStatus = (typeof PROJECT_STATUSES)[number]

export interface ProjectListFilters {
  include_archived?: boolean
}

export const projectKeys = {
  all: ["projects"] as const,
  lists: () => [...projectKeys.all, "list"] as const,
  list: (f: ProjectListFilters) => [...projectKeys.lists(), f] as const,
  detail: (id: number) => [...projectKeys.all, "detail", id] as const,
  docs: (id: number) => [...projectKeys.all, "doc", id] as const,
  doc: (id: number, folder: string, slug: string) => [...projectKeys.docs(id), folder, slug] as const,
  activity: (id: number) => [...projectKeys.all, "activity", id] as const,
  orchestrator: (id: number) => [...projectKeys.all, "orchestrator", id] as const,
}

// ------------------------------------------------------------------ queries

export const projectsQuery = (f: ProjectListFilters = {}) =>
  queryOptions({
    queryKey: projectKeys.list(f),
    queryFn: ({ signal }) =>
      api.get<ListResponse<ProjectSummary>>("/projects", {
        signal,
        query: { include_archived: f.include_archived ? "true" : undefined },
      }),
    select: (r) => r.items,
    placeholderData: keepPreviousData,
  })

export const useProjects = (f: ProjectListFilters = {}) => useQuery(projectsQuery(f))

export const projectQuery = (id: number) =>
  queryOptions({
    queryKey: projectKeys.detail(id),
    queryFn: ({ signal }) => api.get<ProjectDetail>(`/projects/${id}`, { signal }),
  })

export const useProject = (id: number) => useQuery(projectQuery(id))

export const projectDocQuery = (id: number, folder: string, slug: string) =>
  queryOptions({
    queryKey: projectKeys.doc(id, folder, slug),
    queryFn: ({ signal }) =>
      api.get<ProjectDocument>(`/projects/${id}/knowledge/docs/${encodeURIComponent(slug)}`, {
        signal,
        query: { folder },
      }),
  })

export const useProjectDoc = (id: number, ref: { folder: string; slug: string } | undefined) =>
  useQuery({ ...projectDocQuery(id, ref?.folder ?? "", ref?.slug ?? ""), enabled: !!ref })

const ACTIVITY_PAGE = 100

export const projectActivityQuery = (id: number) =>
  infiniteQueryOptions({
    queryKey: projectKeys.activity(id),
    queryFn: ({ signal, pageParam }) =>
      api.get<ListResponse<ProjectActivityEntry>>(`/projects/${id}/activity`, {
        signal,
        query: { limit: ACTIVITY_PAGE, before_id: pageParam },
      }),
    initialPageParam: undefined as number | undefined,
    // pages come back oldest → newest; the next (older) page starts before the first entry
    getNextPageParam: (last) => (last.items.length < ACTIVITY_PAGE ? undefined : last.items[0]?.id),
  })

/** Newest first, flattened across pages. */
export const useProjectActivity = (id: number) =>
  useInfiniteQuery({
    ...projectActivityQuery(id),
    select: (d: InfiniteData<ListResponse<ProjectActivityEntry>, number | undefined>) =>
      d.pages.flatMap((p) => p.items.slice().reverse()),
  })

export const orchestratorQuery = (id: number) =>
  queryOptions({
    queryKey: projectKeys.orchestrator(id),
    queryFn: ({ signal }) => api.get<OrchestratorPayload>(`/projects/${id}/orchestrator`, { signal }),
  })

export const useOrchestrator = (id: number) => useQuery(orchestratorQuery(id))

// ------------------------------------------------------------------ projects

export interface ProjectInput {
  name?: string
  description?: string
  color?: string
  status?: ProjectStatus
  auto_run_enabled?: boolean
  max_auto_resume_count?: number
  max_consecutive_task_failures?: number
}

export function useCreateProject() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<ProjectDetail, ProjectInput>({
    mutationFn: (body) => api.post("/projects", body),
    onSuccess: (p) => qc.setQueryData(projectKeys.detail(p.id), p),
    invalidates: [projectKeys.lists()],
    successToast: (p) => t("tasks.projects.toast.created", { name: p.name }),
    errorToast: "unlessFields",
  })
}

export function useUpdateProject() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<ProjectDetail, { id: number; silent?: boolean } & ProjectInput>({
    mutationFn: ({ id, silent: _s, ...body }) => api.patch(`/projects/${id}`, body),
    onSuccess: (p) => qc.setQueryData(projectKeys.detail(p.id), p),
    invalidates: (p) => [projectKeys.lists(), projectKeys.orchestrator(p.id), projectKeys.activity(p.id)],
    successToast: (p, v) => !v.silent && t("tasks.projects.toast.saved", { name: p.name }),
    errorToast: "unlessFields",
  })
}

export function useArchiveProject() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<ProjectDetail, Pick<ProjectSummary, "id" | "name">>({
    mutationFn: (p) => api.post(`/projects/${p.id}/archive`),
    onSuccess: (p) => qc.setQueryData(projectKeys.detail(p.id), p),
    invalidates: (p) => [projectKeys.lists(), projectKeys.activity(p.id)],
    successToast: (p) => t("tasks.projects.toast.archived", { name: p.name }),
  })
}

export function useDeleteProject() {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<void, Pick<ProjectSummary, "id" | "name">>({
    mutationFn: (p) => api.delete(`/projects/${p.id}`),
    onSuccess: (_d, p) => {
      qc.removeQueries({ queryKey: projectKeys.detail(p.id) })
      qc.removeQueries({ queryKey: projectKeys.orchestrator(p.id) })
      qc.removeQueries({ queryKey: projectKeys.activity(p.id) })
    },
    invalidates: [projectKeys.lists(), ["tasks"]],
    successToast: (_d, p) => t("tasks.projects.toast.deleted", { name: p.name }),
  })
}

/** Fetches the full audit export (JSON). The caller turns it into a download. */
export function useExportProject() {
  const { t } = useTranslation()
  return useApiMutation<unknown, number>({
    mutationFn: (id) => api.get(`/projects/${id}/export`),
    successToast: t("tasks.projects.export.done"),
  })
}

// ------------------------------------------------------------------ shared data

export function useSetProjectData(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<ProjectDataEntry, { key: string; value: string }>({
    mutationFn: ({ key, value }) =>
      api.put(`/projects/${projectId}/data/${encodeURIComponent(key)}`, { value }),
    invalidates: [projectKeys.detail(projectId)],
    successToast: (e) => t("tasks.projects.data.saved", { key: e.key }),
    errorToast: "unlessFields",
  })
}

export function useDeleteProjectData(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<void, string>({
    mutationFn: (key) => api.delete(`/projects/${projectId}/data/${encodeURIComponent(key)}`),
    invalidates: [projectKeys.detail(projectId)],
    successToast: (_d, key) => t("tasks.projects.data.deleted", { key }),
  })
}

// ------------------------------------------------------------------ rules

export interface RuleInput {
  text?: string
  order?: number
  enabled?: boolean
}

function patchRules(
  qc: ReturnType<typeof useQueryClient>,
  projectId: number,
  fn: (rules: ProjectRule[]) => ProjectRule[],
) {
  qc.setQueryData<ProjectDetail>(projectKeys.detail(projectId), (old) =>
    old ? { ...old, rules: fn(old.rules) } : old,
  )
}

export function useCreateRule(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<ProjectRule, RuleInput>({
    mutationFn: (body) => api.post(`/projects/${projectId}/rules`, body),
    invalidates: [projectKeys.detail(projectId), projectKeys.activity(projectId)],
    successToast: t("tasks.projects.rules.added"),
    errorToast: "unlessFields",
  })
}

/**
 * Optimistic: toggles and reorders apply instantly. A reorder sends several PATCHes, so the
 * project is refetched only once the last in-flight rule update settles — an earlier refetch
 * could land after a later PATCH and overwrite its optimistic state.
 */
export function useUpdateRule(projectId: number) {
  const qc = useQueryClient()
  const mutationKey = [...projectKeys.detail(projectId), "update-rule"]
  return useApiMutation<ProjectRule, { id: number } & RuleInput, { prev?: ProjectDetail }>({
    mutationKey,
    mutationFn: ({ id, ...body }) => api.patch(`/projects/${projectId}/rules/${id}`, body),
    onMutate: async ({ id, ...body }) => {
      await qc.cancelQueries({ queryKey: projectKeys.detail(projectId) })
      const prev = qc.getQueryData<ProjectDetail>(projectKeys.detail(projectId))
      patchRules(qc, projectId, (rules) =>
        rules
          .map((r) => (r.id === id ? { ...r, ...stripUndefined(body) } : r))
          .sort((a, b) => a.order - b.order || a.id - b.id),
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => {
      // with other updates in flight, the refetch below restores the truth instead
      if (ctx?.prev && qc.isMutating({ mutationKey }) === 1)
        qc.setQueryData(projectKeys.detail(projectId), ctx.prev)
    },
    onSettled: () =>
      qc.isMutating({ mutationKey }) === 1
        ? qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) })
        : undefined,
  })
}

export function useDeleteRule(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<void, number>({
    mutationFn: (id) => api.delete(`/projects/${projectId}/rules/${id}`),
    invalidates: [projectKeys.detail(projectId)],
    successToast: t("tasks.projects.rules.deleted"),
  })
}

function stripUndefined<T extends object>(o: T): Partial<T> {
  return Object.fromEntries(Object.entries(o).filter(([, v]) => v !== undefined)) as Partial<T>
}

// ------------------------------------------------------------------ files

export function useUploadProjectFile(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<ProjectFileUpload, File>({
    mutationFn: (file) => {
      const fd = new FormData()
      fd.append("file", file, file.name)
      return api.post(`/projects/${projectId}/files`, fd)
    },
    invalidates: [projectKeys.detail(projectId), projectKeys.activity(projectId)],
    successToast: (r) =>
      r.duplicate
        ? t("tasks.projects.files.duplicate", { name: r.file.original_filename })
        : t("tasks.projects.files.uploaded", { name: r.file.original_filename }),
  })
}

export function useDeleteProjectFile(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<void, { id: number; name: string }>({
    mutationFn: (f) => api.delete(`/projects/${projectId}/files/${f.id}`),
    invalidates: [projectKeys.detail(projectId), projectKeys.activity(projectId)],
    successToast: (_d, f) => t("tasks.projects.files.deleted", { name: f.name }),
  })
}

// ------------------------------------------------------------------ knowledge

export interface DocInput {
  title: string
  folder: string
  content: string
}

export function useSaveProjectDoc(projectId: number) {
  const { t } = useTranslation()
  const qc = useQueryClient()
  return useApiMutation<ProjectDocument, DocInput & { previous?: { folder: string; slug: string } }>({
    mutationFn: async ({ previous, ...body }) => {
      const doc = await api.put<ProjectDocument>(`/projects/${projectId}/knowledge`, body)
      // documents are keyed by folder + slug(title): a rename or move creates a new row
      if (previous && (previous.folder !== doc.folder || previous.slug !== doc.slug)) {
        await api.delete(`/projects/${projectId}/knowledge/docs/${encodeURIComponent(previous.slug)}`, {
          query: { folder: previous.folder },
        })
      }
      return doc
    },
    onSuccess: (d) => qc.setQueryData(projectKeys.doc(projectId, d.folder, d.slug), d),
    invalidates: [projectKeys.detail(projectId), projectKeys.activity(projectId)],
    successToast: (d) => t("tasks.projects.knowledge.saved", { title: d.title }),
    errorToast: "unlessFields",
  })
}

export function useDeleteProjectDoc(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<void, { folder: string; slug: string; title: string }>({
    mutationFn: (d) =>
      api.delete(`/projects/${projectId}/knowledge/docs/${encodeURIComponent(d.slug)}`, {
        query: { folder: d.folder },
      }),
    invalidates: [projectKeys.detail(projectId), projectKeys.activity(projectId)],
    successToast: (_d, d) => t("tasks.projects.knowledge.deleted", { title: d.title }),
  })
}

export function useCreateKnowledgeFolder(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<KnowledgeFolder, string>({
    mutationFn: (path) => api.post(`/projects/${projectId}/knowledge/folders`, { path }),
    invalidates: [projectKeys.detail(projectId), projectKeys.activity(projectId)],
    successToast: (f) => t("tasks.projects.knowledge.folderCreated", { path: f.path }),
    errorToast: "unlessFields",
  })
}

export function useDeleteKnowledgeFolder(projectId: number) {
  const { t } = useTranslation()
  return useApiMutation<void, string>({
    mutationFn: (path) => api.delete(`/projects/${projectId}/knowledge/folders`, { query: { path } }),
    invalidates: [projectKeys.detail(projectId)],
    successToast: (_d, path) => t("tasks.projects.knowledge.folderDeleted", { path }),
  })
}

// ------------------------------------------------------------------ orchestrator

export interface OrchestratorConfigInput {
  orchestrator_enabled?: boolean
  orchestrator_goal?: string
  orchestrator_pass_budget?: number
  orchestrator_agent_id?: number | null
  orchestrator_interval_minutes?: number | null
}

function useSetOrchestrator(projectId: number) {
  const qc = useQueryClient()
  return (p: OrchestratorPayload) => {
    qc.setQueryData(projectKeys.orchestrator(projectId), p)
    void qc.invalidateQueries({ queryKey: projectKeys.detail(projectId) })
  }
}

export function useConfigureOrchestrator(projectId: number) {
  const { t } = useTranslation()
  const set = useSetOrchestrator(projectId)
  return useApiMutation<OrchestratorPayload, OrchestratorConfigInput>({
    mutationFn: (body) => api.patch(`/projects/${projectId}/orchestrator`, body),
    onSuccess: set,
    invalidates: [projectKeys.lists()],
    successToast: t("tasks.projects.orchestrator.saved"),
    errorToast: "unlessFields",
  })
}

/** Queues a pass; with a message it is sent as a chat turn. Progress arrives on the project stream. */
export function useWakeOrchestrator(projectId: number) {
  const set = useSetOrchestrator(projectId)
  return useApiMutation<OrchestratorPayload, { message?: string }>({
    mutationFn: ({ message }) =>
      message
        ? api.post(`/projects/${projectId}/orchestrator/chat`, { message })
        : api.post(`/projects/${projectId}/orchestrator/run`, {}),
    onSuccess: set,
  })
}

export function useResetOrchestrator(projectId: number) {
  const { t } = useTranslation()
  const set = useSetOrchestrator(projectId)
  return useApiMutation<OrchestratorPayload, void>({
    mutationFn: () => api.post(`/projects/${projectId}/orchestrator/reset`),
    onSuccess: set,
    invalidates: [projectKeys.activity(projectId)],
    successToast: t("tasks.projects.orchestrator.resetDone"),
  })
}
